// src/pages/qbo/AuditTab.tsx
//
// Reconciliation audit (agent 3.19.3+): run it, read the latest summary,
// download the workbook (one sheet per section plus Summary and Actions),
// and post the one-time ONACCT journal for pre-cutoff on-account cash.
// The audit itself is read-only; the journal is an explicit, confirmed step.

import { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { AlertTriangle, Download, Play, ShieldAlert } from 'lucide-react';
import qboApi from '../../services/qboApi';
import { fmtLocalTimeOr } from '../../lib/formatTime';
import { money } from './qboTypes';
import { runJob, type JobView } from './qboJobs';
import SyncResultList from './SyncResultList';
import ResyncCard from './ResyncCard';
import PreCashCard from './PreCashCard';
import type { SyncResult } from './qboTypes';

interface Props {
  onMessage: (type: 'error' | 'success' | 'info', text: string) => void;
}

interface AuditMeta {
  id: string;
  takenUtc: string;
  cutoff: string;
  summary: Record<string, number | string>;
  notes: string[];
  sections: Record<string, number>;
}

type Row = Record<string, unknown>;

const SECTIONS = ['customers', 'documents', 'receipts', 'strays', 'onAccount', 'overApplied', 'unconverted', 'placeholder'] as const;

const SUMMARY_LABELS: [string, string][] = [
  ['advantageCustomerBalances', 'Advantage: open documents, all customers'],
  ['advantageBalanceFieldTotal', 'Advantage: Customer.Balance field (lags)'],
  ['quickBooksCustomerBalances', 'QuickBooks: linked customers'],
  ['quickBooksPlaceholder', 'QuickBooks: placeholder'],
  ['quickBooksTotalAr', 'QuickBooks: total A/R'],
  ['customersListed', 'Customers listed'],
  ['customersInBalance', '  in balance'],
  ['customersExplained', '  difference explained'],
  ['customersUnexplained', '  unexplained residual'],
  ['receiptsSinceCutoff', 'Receipts since the cutoff'],
  ['receiptsNeverPushed', '  never pushed'],
  ['receiptsCapped', '  capped (overpaid in Advantage, expected)'],
  ['receiptsVoided', '  voided in Advantage (bounced checks)'],
  ['receiptsInError', '  in error'],
  ['receiptsDiffer', '  pushed but differing / missing'],
  ['documentsListed', 'Documents open on either side'],
  ['strays', 'Desktop-era strays'],
  ['straysTotal', '  strays total'],
  ['preCutoffOnAccountRows', 'Pre-cutoff on-account receipts'],
  ['preCutoffOnAccountTotal', '  on-account total'],
  ['overApplied', 'Over-applied receipts'],
  ['unconverted', 'Documents open at the cutoff, not converted'],
  ['unconvertedTotal', '  not converted total'],
  ['duration', 'Run time'],
];
const MONEY_KEYS = new Set(['advantageCustomerBalances', 'advantageBalanceFieldTotal', 'quickBooksCustomerBalances', 'quickBooksPlaceholder', 'quickBooksTotalAr', 'straysTotal', 'preCutoffOnAccountTotal', 'unconvertedTotal']);

async function fetchSection(id: string, section: string): Promise<Row[]> {
  const rows: Row[] = [];
  for (let page = 1; page < 500; page++) {
    const r = (await qboApi.auditSectionPage(id, section, page)) as { rows: Row[]; pages: number };
    rows.push(...(r.rows || []));
    if (page >= (r.pages || 1)) break;
  }
  return rows;
}

function buildWorkbook(meta: AuditMeta, data: Record<string, Row[]>): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  const cust = data.customers || [];

  // Two Advantage codes sharing one QuickBooks customer (name match at link time): compare them together.
  const byQbo = new Map<string, number[]>();
  for (const c of cust) if (c.qboId) byQbo.set(String(c.qboId), [...(byQbo.get(String(c.qboId)) || []), Number(c.code)]);
  for (const c of cust) {
    const mates = (byQbo.get(String(c.qboId || '')) || []).filter((x) => x !== Number(c.code));
    c.sharedWith = mates.join(', ');
    if (mates.length) c.verdict = `shares QuickBooks customer with code(s) ${mates.join(', ')} — compare the codes together`;
  }
  // An Advantage-open document with no sync row may exist in QuickBooks from the Desktop era, paid there.
  const existsInQbo = new Map<string, Row>();
  for (const u of data.unconverted || []) if (u.cannotConvert) existsInQbo.set(String(u.key), u);
  for (const d of data.documents || []) {
    if (d.side === 'Advantage' && String(d.state || '').startsWith('open in Advantage, not in QuickBooks')) {
      const u = existsInQbo.get(String(d.docNumber));
      if (u) { d.state = 'open in Advantage, exists in QuickBooks (Desktop-era) but closed there — bookkeeper decides which side is right'; d.paidBy = String(u.warnings || '').slice(0, 200); }
    }
  }

  const summaryRows: (string | number)[][] = [
    ['Advantage ↔ QuickBooks reconciliation audit'],
    [`Audit ${meta.id} taken ${meta.takenUtc} UTC · conversion cutoff ${meta.cutoff}`],
    [],
    ['Measure', 'Value'],
    ...SUMMARY_LABELS.filter(([k]) => k in meta.summary).map(([k, label]) => [label, meta.summary[k] as string | number]),
    [],
    ['How to read this workbook'],
    ...meta.notes.map((n) => [n]),
    [],
    ['Sheets'],
    ['Customers', 'one row per customer: both balances, the difference, and what explains it; start with verdict = unexplained'],
    ['Documents', "every document open on either side; filter state for 'paid in Advantage, open in QuickBooks' and the reverse"],
    ['Receipts', 'every receipt since the cutoff with its push state and any difference against the QuickBooks payment'],
    ['Strays', 'Desktop-era items still on customers, with the Advantage receipt each duplicates — the cleanup list'],
    ['On account', 'pre-cutoff on-account cash per customer — the ONACCT journal moves it from the placeholder'],
    ['Over-applied', 'receipts that apply more than a document is worth, with the effect of posting them capped'],
    ['Unconverted', 'documents open at the cutoff with no QuickBooks counterpart'],
    ['Placeholder', 'what the placeholder balance consists of'],
    ['Actions', 'recommended action per finding, grouped by who does it'],
  ];
  const ws0 = XLSX.utils.aoa_to_sheet(summaryRows);
  ws0['!cols'] = [{ wch: 48 }, { wch: 110 }];
  XLSX.utils.book_append_sheet(wb, ws0, 'Summary');

  const add = (name: string, rows: Row[], cols: [string, string][], widths?: Record<string, number>) => {
    const aoa: unknown[][] = [cols.map(([h]) => h)];
    for (const r of rows) aoa.push(cols.map(([, k]) => { const v = r[k]; return v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : v; }));
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = cols.map(([h, k]) => ({ wch: widths?.[k] ?? Math.max(10, Math.min(60, Math.max(h.length, ...rows.slice(0, 300).map((r) => String(r[k] ?? '').length)) + 2)) }));
    ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: Math.max(rows.length, 1), c: cols.length - 1 } }) };
    ws['!freeze'] = { xSplit: 0, ySplit: 1 } as unknown as XLSX.WorkSheet['!freeze'];
    XLSX.utils.book_append_sheet(wb, ws, name);
  };

  add('Customers', cust, [
    ['Code', 'code'], ['Customer', 'name'], ['QBO id', 'qboId'], ['Advantage open documents', 'adsBalance'], ['Advantage balance field', 'adsBalanceField'], ['Field lag', 'adsFieldLag'], ['QuickBooks balance', 'qboBalance'], ['Difference (QBO − ADS docs)', 'difference'],
    ['Receipts not pushed', 'unpushedReceipts'], ['Pushed, differs', 'pushedDiffers'], ['Desktop strays', 'strays'], ['Pre-cutoff on account', 'preCutoffOnAccount'], ['Unconverted docs', 'unconverted'],
    ['Explained', 'explained'], ['Residual', 'residual'], ['Verdict', 'verdict'], ['Shares QBO customer with', 'sharedWith'],
    ['Docs paid in ADS, open in QBO', 'docsPaidInAdsOpenInQbo'], ['Open both, balance differs', 'docsOpenBothDifference'], ['QBO unapplied since cutoff', 'qboUnappliedSinceCutoff'],
  ]);
  add('Documents', data.documents || [], [
    ['Side', 'side'], ['Type', 'type'], ['Doc #', 'docNumber'], ['QBO id', 'qboId'], ['Date', 'date'], ['Code', 'code'], ['Customer', 'customer'],
    ['Total', 'total'], ['QBO balance', 'qboBalance'], ['ADS balance', 'adsBalance'], ['State', 'state'], ['Sync action', 'syncAction'], ['Paid in Advantage by', 'paidBy'],
  ], { paidBy: 60, state: 48 });
  add('Receipts', data.receipts || [], [
    ['Key', 'key'], ['Receipt', 'receipt'], ['Code', 'code'], ['Customer', 'customer'], ['Kind', 'kind'], ['Date', 'date'], ['Cash date', 'cashDate'], ['Check', 'check'],
    ['Amount', 'amount'], ['Applied', 'applied'], ['Lines', 'lines'], ['Taken by', 'takenBy'], ['Status', 'status'], ['QBO id', 'qboId'], ['Sync action', 'syncAction'], ['Last sync', 'lastSync'],
    ['QBO unapplied', 'qboUnapplied'], ['Difference', 'difference'], ['Changed since push', 'headerChanged'], ['Detail', 'detail'],
  ], { detail: 70 });
  add('Strays', data.strays || [], [
    ['QBO customer id', 'qboCustomerId'], ['Customer', 'customer'], ['Code', 'code'], ['Type', 'type'], ['Date', 'date'], ['Num', 'num'], ['Open', 'open'], ['Why', 'why'], ['Advantage', 'advantage'], ['Already cleared', 'alreadyCleared'],
  ], { advantage: 80 });
  add('On account', data.onAccount || [], [
    ['Receipt', 'receipt'], ['Code', 'code'], ['Customer', 'customer'], ['Cash date', 'cashDate'], ['Check', 'check'], ['Amount', 'amount'], ['QBO customer id', 'qboCustomerId'],
  ]);
  add('Over-applied', data.overApplied || [], [
    ['Key', 'key'], ['Code', 'code'], ['Customer', 'customer'], ['Kind', 'kind'], ['Date', 'date'], ['Cash date', 'cashDate'], ['Check', 'check'], ['Amount', 'amount'], ['Lines', 'lines'], ['Status', 'status'], ['Balance effect', 'balanceEffect'], ['Detail', 'detail'],
  ], { detail: 90 });
  add('Unconverted', data.unconverted || [], [
    ['Doc #', 'key'], ['Type', 'type'], ['Date', 'date'], ['Code', 'code'], ['Customer', 'name'], ['Open at cutoff', 'open'], ['Total', 'total'], ['Cannot convert', 'cannotConvert'], ['Warnings', 'warnings'],
  ], { warnings: 80 });
  add('Placeholder', data.placeholder || [], [['Item', 'item'], ['Amount', 'amount'], ['Note', 'note']], { item: 80, note: 50 });

  // Actions: one line per finding, grouped by who acts on it.
  const actions: Row[] = [];
  for (const c of cust) {
    if (c.sharedWith) actions.push({ who: 'Bookkeeper', what: `Codes ${c.code} and ${c.sharedWith} (${c.name}) share one QuickBooks customer: merge the Advantage codes or split the QuickBooks customer`, amount: c.difference, source: 'Customers' });
    if (String(c.verdict || '').startsWith('unexplained')) actions.push({ who: 'Joel / agent', what: `Customer ${c.code} ${c.name}: residual ${money(Number(c.residual))} — review Documents and Receipts for this code`, amount: c.residual, source: 'Customers' });
  }
  for (const r of data.receipts || []) {
    const st = String(r.status || '');
    if (st === 'never pushed') actions.push({ who: 'Office', what: `Push receipt ${r.key} (${r.customer}, ${r.cashDate}, ${money(Number(r.amount))})`, amount: r.amount, source: 'Receipts' });
    else if (st === 'error') actions.push({ who: 'Office', what: `Receipt ${r.key} (${r.customer}): ${r.detail || ''}`, amount: r.amount, source: 'Receipts' });
    else if ((st.startsWith('pushed,') && !st.startsWith('pushed, capped')) || r.headerChanged) actions.push({ who: 'Office / Joel', what: `Receipt ${r.key} (${r.customer}) ${st}: ${r.detail || ''} — re-push after checking`, amount: r.difference || 0, source: 'Receipts' });
  }
  for (const s of data.strays || []) actions.push({ who: 'Bookkeeper (cleanup card)', what: `${s.customer}: ${s.type} ${s.date} ${s.num} ${money(Number(s.open))} — ${s.advantage || s.why}`, amount: s.open, source: 'Strays' });
  const onAcct = new Map<string, number>();
  for (const o of data.onAccount || []) { const k = `${o.code}|${o.customer}`; onAcct.set(k, (onAcct.get(k) || 0) + Number(o.amount || 0)); }
  for (const [k, amt] of [...onAcct.entries()].sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))) { const [code, name] = k.split('|'); actions.push({ who: 'Agent (ONACCT journal)', what: `Move ${money(amt)} of pre-cutoff on-account cash from the placeholder to ${name} (${code})`, amount: amt, source: 'On account' }); }
  for (const o of data.overApplied || []) actions.push({ who: 'Office', what: `Receipt ${o.key} (${o.customer}): correct in Advantage — ${o.detail}`, amount: o.amount, source: 'Over-applied' });
  for (const u of data.unconverted || []) if (!u.cannotConvert) actions.push({ who: 'Joel (Conversion tab)', what: `Convert ${u.key} (${u.name}, ${u.date}, open ${money(Number(u.open))})`, amount: u.open, source: 'Unconverted' });
  add('Actions', actions, [['Who', 'who'], ['What', 'what'], ['Amount', 'amount'], ['Source sheet', 'source']], { what: 110 });
  return wb;
}

export default function AuditTab({ onMessage }: Props) {
  const [meta, setMeta] = useState<AuditMeta | null>(null);
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState<JobView | null>(null);
  // 3.25.0: receipts QuickBooks applied differently than Advantage (capped pushes, applications that landed on
  // other documents): re-push them with the current Advantage lines, capped where Advantage over-applies.
  const [repushKeys, setRepushKeys] = useState<string[] | null>(null);
  // 3.25.5: codes the latest audit left unexplained, offered to the pre-cutoff cash card
  const [unexplainedCodes, setUnexplainedCodes] = useState<number[]>([]);
  const [repushing, setRepushing] = useState(false);
  const [repushJob, setRepushJob] = useState<JobView | null>(null);
  const [repushResult, setRepushResult] = useState<SyncResult | null>(null);
  const pages = async (id: string, section: string): Promise<Row[]> => {
    let rows: Row[] = [];
    for (let page = 1; page < 60; page++) {
      const r = (await qboApi.auditSectionPage(id, section, page)) as { rows: Row[]; pages?: number };
      rows = rows.concat(r.rows || []);
      if (!r.rows?.length || (r.pages && page >= r.pages)) break;
    }
    return rows;
  };
  const findRepush = async () => {
    if (!meta) return;
    try {
      const rc = await pages(meta.id, 'receipts');
      const docs = await pages(meta.id, 'documents');
      const keys = new Set<string>();
      // 3.25.3: 'pushed, capped (overpaid in Advantage)' is the expected shape (excess kept unapplied) — not re-pushed.
      for (const r of rc) { const st = String(r.status || ''); if (st.startsWith('pushed,') && !st.startsWith('pushed, capped')) keys.add(String(r.key)); }
      for (const d of docs) {
        if (!String(d.state || '').startsWith('paid in Advantage')) continue;
        for (const m of String(d.paidBy || '').matchAll(/(\d+:\d+) [\d-]+ [-\d.]+ \[pushed/g)) keys.add(m[1]);
      }
      setRepushKeys([...keys]);
      onMessage('info', `${keys.size} receipt(s) to re-push from audit ${meta.id}`);
      try {
        const cs = await pages(meta.id, 'customers');
        setUnexplainedCodes(cs.filter((c) => String(c.verdict || '').startsWith('unexplained')).map((c) => Number(c.code)).filter((n) => n > 0));
      } catch { /* optional */ }
    } catch (e: unknown) { onMessage('error', (e as Error).message); }
  };
  const repush = async () => {
    if (!repushKeys || repushKeys.length === 0) return;
    setRepushing(true); setRepushResult(null); setRepushJob(null);
    try {
      const r = await runJob(() => qboApi.push('payments', repushKeys, { allowCap: true }), setRepushJob);
      setRepushResult(r);
      onMessage(r.errors ? 'error' : 'success', `Re-push: ${r.created} created, ${r.updated} updated, ${r.skipped} skipped, ${r.errors} errors`);
      setRepushKeys(null);
    } catch (e: unknown) { onMessage('error', (e as Error).message); }
    finally { setRepushing(false); setRepushJob(null); }
  };
  const [downloading, setDownloading] = useState<string | null>(null);
  const [onAccount, setOnAccount] = useState<{ cutoff: string; customers: number; receipts: number; total: number; unlinked: number; alreadyPosted: { docNumber: string; qboId: string; when?: string }[]; rows: { code: number; customer: string; amount: number; receipts: number; qboCustomerId?: string | null }[] } | null>(null);
  const [confirmingJournal, setConfirmingJournal] = useState(false);
  const [journalResult, setJournalResult] = useState<SyncResult | null>(null);
  const [baselineResult, setBaselineResult] = useState<SyncResult | null>(null);

  const loadLatest = async () => {
    try { setMeta((await qboApi.auditLatest()) as AuditMeta); }
    catch { setMeta(null); }
  };
  useEffect(() => { loadLatest(); }, []);

  const run = async () => {
    setRunning(true); setJob(null);
    try {
      const r = await runJob(() => qboApi.auditRun(), setJob, 3000);
      onMessage(r.errors ? 'error' : 'success', `Audit ${r.errors ? 'failed' : 'done'} in ${r.duration}`);
      await loadLatest();
    } catch (e: unknown) { onMessage('error', (e as Error).message); }
    finally { setRunning(false); setJob(null); }
  };

  const download = async () => {
    if (!meta) return;
    setDownloading('fetching…');
    try {
      const data: Record<string, Row[]> = {};
      for (const s of SECTIONS) { setDownloading(`fetching ${s}…`); data[s] = await fetchSection(meta.id, s); }
      setDownloading('building workbook…');
      const wb = buildWorkbook(meta, data);
      XLSX.writeFile(wb, `Devash-QBO-reconciliation-audit-${meta.id}.xlsx`);
      onMessage('success', 'Workbook downloaded');
    } catch (e: unknown) { onMessage('error', (e as Error).message); }
    finally { setDownloading(null); }
  };

  const loadOnAccount = async () => {
    try { setOnAccount(await qboApi.onAccountPlan() as typeof onAccount); }
    catch (e: unknown) { onMessage('error', (e as Error).message); }
  };

  const postJournal = async () => {
    if (!confirmingJournal) { setConfirmingJournal(true); setTimeout(() => setConfirmingJournal(false), 8000); return; }
    setConfirmingJournal(false);
    try {
      const r = (await qboApi.onAccountPost()) as SyncResult;
      setJournalResult(r);
      onMessage(r.errors ? 'error' : 'success', `ONACCT journal: ${r.created} created, ${r.updated} updated, ${r.skipped} skipped, ${r.errors} errors`);
      await loadOnAccount();
    } catch (e: unknown) { onMessage('error', (e as Error).message); }
  };

  const baseline = async () => {
    try {
      const r = (await qboApi.paymentsBaseline()) as SyncResult;
      setBaselineResult(r);
      onMessage(r.errors ? 'error' : 'success', `Baseline: ${r.updated} receipt(s) now tracked, ${r.skipped} already were, ${r.errors} errors`);
    } catch (e: unknown) { onMessage('error', (e as Error).message); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <div className="mr-auto">
            <div className="text-sm font-semibold text-slate-400">Reconciliation audit</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Advantage and QuickBooks side by side: every customer, every open document, every receipt since the cutoff. Read-only; about two minutes.</div>
          </div>
          <button className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-50 inline-flex items-center gap-2" onClick={run} disabled={running}>
            <Play size={14} /> {running ? (job ? `Running ${job.done}/${job.total}…` : 'Starting…') : 'Run audit'}
          </button>
          <button className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-600 text-slate-200 hover:bg-slate-700 transition-colors disabled:opacity-50 inline-flex items-center gap-2" onClick={download} disabled={!meta || !!downloading || running}>
            <Download size={14} /> {downloading || 'Download workbook (.xlsx)'}
          </button>
        </div>
        {meta ? (
          <>
            <div className="text-xs text-slate-400 mb-2">Latest audit <span className="font-mono">{meta.id}</span> · taken {fmtLocalTimeOr(meta.takenUtc, meta.takenUtc)} · cutoff {meta.cutoff}</div>
            <table className="text-sm border-collapse">
              <tbody>
                {SUMMARY_LABELS.filter(([k]) => k in meta.summary).map(([k, label]) => (
                  <tr key={k}>
                    <td className={`pr-6 py-0.5 ${label.startsWith('  ') ? 'pl-4 text-slate-400' : 'text-slate-300'}`}>{label.trim()}</td>
                    <td className="py-0.5 text-right font-mono text-slate-200">{MONEY_KEYS.has(k) ? `$${money(Number(meta.summary[k]))}` : String(meta.summary[k])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 text-[11px] text-slate-500">Sections: {Object.entries(meta.sections).map(([s, n]) => `${s} ${n}`).join(' · ')}</div>
          </>
        ) : (
          <div className="text-sm text-slate-500">No audit yet (needs agent 3.19.3 or newer). Run one.</div>
        )}
      </div>

      <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
        <div className="flex flex-wrap items-center gap-3">
          <div className="mr-auto">
            <div className="text-sm font-semibold text-slate-400">Receipts QuickBooks applied differently</div>
            <div className="text-[11px] text-slate-500 mt-0.5">From the latest audit: pushed receipts whose QuickBooks lines differ from Advantage, plus receipts that paid a document Advantage shows paid but QuickBooks still shows open. Re-pushing rewrites each payment with Advantage's current applications, capped where Advantage over-applies. Advantage is the source of truth.</div>
          </div>
          <button className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-600 text-slate-200 hover:bg-slate-700 transition-colors disabled:opacity-50" onClick={findRepush} disabled={!meta || repushing}>Find them</button>
          {repushKeys && repushKeys.length > 0 && (
            <button className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-500 transition-colors disabled:opacity-50" onClick={repush} disabled={repushing}>
              {repushing ? (repushJob ? `Re-pushing ${repushJob.done}/${repushJob.total}…` : 'Starting…') : `Re-push ${repushKeys.length} receipt(s)`}
            </button>
          )}
        </div>
        {repushKeys && <div className="text-[11px] text-slate-400 mt-2 font-mono break-all">{repushKeys.length ? repushKeys.join(' ') : 'nothing to re-push'}</div>}
        <SyncResultList result={repushResult} title="Re-push" />
      </div>

      <PreCashCard onMessage={onMessage} suggestedCodes={unexplainedCodes} />

      <ResyncCard onMessage={onMessage} auditId={meta?.id ?? null} />

      <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
        <div className="flex flex-wrap items-center gap-3 mb-2">
          <div className="mr-auto">
            <div className="text-sm font-semibold text-slate-400">Pre-cutoff on-account cash</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Cash left on customers' accounts by receipts dated on/before the cutoff. It is inside the legacy journals, so QuickBooks holds it in the placeholder. One journal per 150 customers moves it to them.</div>
          </div>
          <button className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-600 text-slate-200 hover:bg-slate-700 transition-colors" onClick={loadOnAccount}>Load</button>
          {onAccount && onAccount.rows.length > 0 && (
            <button className={`px-3 py-1.5 rounded-lg text-xs font-medium text-white transition-colors ${confirmingJournal ? 'bg-red-600 hover:bg-red-500' : 'bg-amber-700 hover:bg-amber-600'}`} onClick={postJournal}>
              {confirmingJournal ? `OK, post ONACCT journal(s) for ${onAccount.customers} customer(s)` : `Post ONACCT journal ($${money(onAccount.total)})`}
            </button>
          )}
        </div>
        {onAccount && (
          <>
            <div className="text-xs text-slate-400 mb-2">
              {onAccount.customers} customers · {onAccount.receipts} receipts · total <span className="font-mono">${money(onAccount.total)}</span>
              {onAccount.unlinked > 0 && <span className="ml-2 text-amber-300"><AlertTriangle size={12} className="inline mr-1" />{onAccount.unlinked} customer(s) not in QuickBooks yet (skipped)</span>}
              {onAccount.alreadyPosted.length > 0 && <span className="ml-2 text-green-300">posted: {onAccount.alreadyPosted.map((p) => `${p.docNumber} → QBO ${p.qboId}`).join(', ')}</span>}
            </div>
            <div className="overflow-x-auto max-h-72 overflow-y-auto rounded-lg border border-slate-700/60">
              <table className="w-full text-sm border-collapse">
                <thead className="sticky top-0 bg-slate-800"><tr>{['Code', 'Customer', 'Receipts', 'Amount', 'QBO customer'].map((h) => <th key={h} className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">{h}</th>)}</tr></thead>
                <tbody>
                  {onAccount.rows.map((r) => (
                    <tr key={r.code} className="hover:bg-slate-700/30">
                      <td className="px-3 py-1 border-b border-slate-800/60 font-mono text-xs">{r.code}</td>
                      <td className="px-3 py-1 border-b border-slate-800/60 text-slate-300">{r.customer}</td>
                      <td className="px-3 py-1 border-b border-slate-800/60 text-slate-400">{r.receipts}</td>
                      <td className="px-3 py-1 border-b border-slate-800/60 font-mono text-xs text-right">{money(r.amount)}</td>
                      <td className="px-3 py-1 border-b border-slate-800/60 text-xs">{r.qboCustomerId ? <span className="text-green-400">#{r.qboCustomerId}</span> : <span className="text-amber-400">not linked</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <SyncResultList result={journalResult} title="ONACCT journal" />
      </div>

      <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
        <div className="flex flex-wrap items-center gap-3">
          <div className="mr-auto">
            <div className="text-sm font-semibold text-slate-400 inline-flex items-center gap-2"><ShieldAlert size={14} /> Change tracking baseline</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Receipts pushed before agent 3.19.4 carry no lines hash. Run once after the update: every pushed receipt gets its hash and cash date, no QuickBooks calls. From then on the Payments review marks receipts changed or deleted in Advantage.</div>
          </div>
          <button className="px-3 py-1.5 rounded-lg text-xs font-medium border border-slate-600 text-slate-200 hover:bg-slate-700 transition-colors" onClick={baseline}>Run baseline</button>
        </div>
        <SyncResultList result={baselineResult} title="Baseline" />
      </div>
    </div>
  );
}
